// src/sections/Service.js

import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Service.css';

import merchandisingImg from '../assets/merchandising.png';
import cocreationIPImg from '../assets/co-creation-ip.jpg';
import creatorPromotionImg from '../assets/creator-promotion.jpg';

gsap.registerPlugin(ScrollTrigger);

const serviceItems = [
  {
    id: 'merchandising',
    title: 'Merchandising',
    japanese: 'マーチャンダイジング（物販）領域',
    description: '「自社EC：ワンスト」及び「オンラインくじプラットフォーム」による\nクリエイターアイテムの企画～販売・受発注生産・流通',
    image: merchandisingImg
  },
  {
    id: 'creator-promotion',
    title: 'Creator Promotion',
    japanese: 'クリエイタープロモーション支援事業',
    description: 'ユーザー数2億人ゲーム"第五人格"とのコラボ事業など\n多様なクリエイターネットワークを活用したプロモーションを展開。\n総合広告代理店出身メンバーによるプロモーション企画～実施を支援。',
    image: creatorPromotionImg
  },
  {
    id: 'co-creation-ip',
    title: 'co-creation IP',
    japanese: '共創IP事業',
    description: 'コミケで人気のクリエイターオリジナルキャラに',
    image: cocreationIPImg
  },
];

function Service() {
  const sectionRef = useRef(null);
  const itemContainersRef = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  
  // インジケーター用のref
  const indicatorListRef = useRef(null);
  const dotRef = useRef(null);
  const barTopRef = useRef(null);
  const barBottomRef = useRef(null);
  const indicatorItemRefs = useRef([]);

  // インジケーターの位置を更新する関数
  const updateIndicators = (targetIndex) => {
    const targetItem = indicatorItemRefs.current[targetIndex];
    if (!targetItem || !indicatorListRef.current || !dotRef.current || !barTopRef.current || !barBottomRef.current) return;

    const indicatorItems = indicatorItemRefs.current.filter(item => item !== null);
    const pinkDot = dotRef.current;
    const barTopEl = barTopRef.current;
    const barBottomEl = barBottomRef.current;
    
    // ピンクの円をターゲット要素へ移動
    const dotHalf = pinkDot.offsetHeight / 2;
    const targetCenter = targetItem.offsetTop + targetItem.offsetHeight / 2;
    gsap.to(pinkDot, {
      top: targetCenter - dotHalf,
      opacity: 1,
      duration: 0.4,
      ease: 'cubic-bezier(0.45, 0, 0.2, 1)',
    });

    if (indicatorItems.length >= 2) {
      const firstItem = indicatorItems[0];
      const lastItem = indicatorItems[indicatorItems.length - 1];
      const firstCenter = firstItem.offsetTop + firstItem.offsetHeight / 2;
      const lastCenter = lastItem.offsetTop + lastItem.offsetHeight / 2;
      // 上部バー: 最初のliの中心からターゲットliの中心-ドット半分まで
      if (targetIndex > 0) {
        gsap.to(barTopEl, {
          top: firstCenter,
          height: (targetCenter - dotHalf) - firstCenter,
          opacity: 1,
          duration: 0.4,
          ease: 'cubic-bezier(0.45, 0, 0.2, 1)',
        });
      } else {
        gsap.to(barTopEl, { opacity: 0, duration: 0.2 });
      }
      // 下部バー: ターゲットliの中心+ドット半分から最後のliの中心まで
      if (targetIndex < indicatorItems.length - 1) {
        gsap.to(barBottomEl, {
          top: targetCenter + dotHalf,
          height: lastCenter - (targetCenter + dotHalf),
          opacity: 1,
          duration: 0.4,
          ease: 'cubic-bezier(0.45, 0, 0.2, 1)',
        });
      } else {
        gsap.to(barBottomEl, { opacity: 0, duration: 0.2 });
      }
    }
  };

  useLayoutEffect(() => {
    ScrollTrigger.getAll().forEach(st => st.kill());
    const section = sectionRef.current;
    if (!section) return;

    // 各アイテムに対して1.5画面分のスクロールを割り当て（より長いスクロール距離）
    const scrollDurationPerItem = window.innerHeight * 1.5;
    const totalScrollDuration = scrollDurationPerItem * serviceItems.length;

    // 初期状態でインジケーターを表示
    const timer = setTimeout(() => {
      updateIndicators(0);
    }, 100);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: `+=${totalScrollDuration}`,
        scrub: 0.5, // scrub値を調整してより応答性を改善
        pin: true,
        anticipatePin: 1,
        refreshPriority: -1,
        onUpdate: (self) => {
          // スクロール進行度に基づいてアクティブなインデックスを計算
          const progress = self.progress;
          let newIndex;
          
          // より適切な境界設定で双方向スクロールを改善
          if (progress <= 0.28) {
            newIndex = 0;
          } else if (progress <= 0.72) {
            newIndex = 1;
          } else {
            newIndex = 2;
          }
          
          if (newIndex !== activeIndex) {
            setActiveIndex(newIndex);
            updateIndicators(newIndex);
          }
        },
      },
    });

    // 各サービスアイテムのアニメーション設定
    serviceItems.forEach((item, index) => {
      const itemEl = itemContainersRef.current[index];
      if (!itemEl) return;

      // より明確な区間分けでアニメーション設定
      const sectionStart = index / 3; // 0, 0.33, 0.66
      const sectionMiddle = sectionStart + 0.15; // 中間で安定表示
      const sectionEnd = (index + 1) / 3; // 0.33, 0.66, 1.0
      const fadeOutStart = sectionEnd - 0.15; // より早めにフェードアウト

      // 初期状態を設定
      gsap.set(itemEl, { 
        opacity: index === 0 ? 1 : 0, 
        y: index === 0 ? 0 : 30,
        scale: index === 0 ? 1 : 0.95
      });

      // 最初のアイテム以外はフェードイン
      if (index > 0) {
        tl.to(itemEl, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.1,
          ease: 'power2.out',
        }, sectionStart);

        // 表示状態を維持
        tl.to(itemEl, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.05,
          ease: 'none',
        }, sectionMiddle);
      }

      // 最後のアイテム以外はフェードアウト
      if (index < serviceItems.length - 1) {
        tl.to(itemEl, {
          opacity: 0,
          y: -30,
          scale: 0.95,
          duration: 0.1,
          ease: 'power2.in',
        }, fadeOutStart);
      }
    });

    // タイムラインの最後に少しの余白を追加
    tl.to({}, { duration: 0.01 }, 1);

    ScrollTrigger.refresh();

    return () => {
      clearTimeout(timer);
      tl.kill();
      ScrollTrigger.getAll().forEach(st => st.kill());
    };
  }, []); // 依存配列からactiveIndexを削除して無限ループを防ぐ

  // activeIndexが変更されたときの追加処理
  useEffect(() => {
    const timer = setTimeout(() => {
      updateIndicators(activeIndex);
    }, 50);
    return () => clearTimeout(timer);
  }, [activeIndex]);

  const handleIndicatorClick = (index) => {
    setActiveIndex(index);
    // スクロール位置を計算してその位置に移動
    const section = sectionRef.current;
    if (section) {
      const sectionTop = section.offsetTop;
      const scrollProgress = index / serviceItems.length;
      const scrollDurationPerItem = window.innerHeight * 1.5;
      const totalScrollDuration = scrollDurationPerItem * serviceItems.length;
      const targetScroll = sectionTop + (totalScrollDuration * scrollProgress);
      
      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="service" className="service-section" ref={sectionRef}>
      <h2 className="section-title">SERVICE</h2>
      <div className="service-items-wrapper">
        {serviceItems.map((item, index) => (
          <div 
            key={item.id}
            ref={(el) => (itemContainersRef.current[index] = el)}
            className="service-item"
          >
            {/* 画像カラム */}
            <div className="service-image-wrapper">
              <img 
                src={item.image} 
                alt={item.title} 
                className={`service-image ${item.id === 'co-creation-ip' ? 'co-creation-image' : ''}`}
              />
            </div>
            {/* 説明カラム */}
            <div className="service-text">
              <h3>{item.title}</h3>
              <p className="japanese-text">{item.japanese}</p>
              <p className="description-text">{item.description.split('\n').map((line, i) => (
                <React.Fragment key={i}>{line}<br/></React.Fragment>
              ))}</p>
            </div>
          </div>
        ))}
        
        {/* インジケーターを独立して配置（PCのみ表示） */}
        <div className="service-indicator-center">
          <ul ref={indicatorListRef} className="service-indicator-list">
            {serviceItems.map((indicatorItem, indicatorIndex) => (
              <li
                key={indicatorItem.id}
                ref={(el) => (indicatorItemRefs.current[indicatorIndex] = el)}
                className={activeIndex === indicatorIndex ? 'active' : ''}
                onClick={() => handleIndicatorClick(indicatorIndex)}
              >
              </li>
            ))}
            <div className="service-indicator-container">
              <div ref={dotRef} className="service-pink-dot"></div>
              <div ref={barTopRef} className="service-connected-bar service-bar-top"></div>
              <div ref={barBottomRef} className="service-connected-bar service-bar-bottom"></div>
            </div>
          </ul>
        </div>
      </div>
    </section>
  );
}

export default Service;